import Vue from 'vue';
import { companyExposer } from '@pirates/not-morty-biz';
import { ActionContext, ActionTree, GetterTree, Module, MutationTree } from 'vuex';
import { Company, CompanyState } from './company.model';
import { t } from '@/utils/translation';
import { keycloak, keycloakB2B } from '@/services/auth/auth.service';
import { AddressValidation, Address } from './components/company-address-form/company-address.model';
import { companyAddressService } from './components/company-address/company-address.service';
import { ContactValidation, Contact } from './components/company-contact-form/company-contact.model';
import { companyContactService } from './components/company-contact/company-contact.service';
import { CompanyType } from '../driver-management/driver-management.model';
import { companyService } from './company.service';

export const GETTER_COMPANY_IDS = 'companies/companyIds';
export const GETTER_CURRENT_COMPANY_ID = 'companies/currentCompanyId';
export const GETTER_IS_COMPANY_CENTRAL_PAYMENT = 'companies/isCompanyCentralPaymentProfile';
export const GETTER_CURRENT_COMPANY_LEGAL_ENTITY_ID = 'companies/currentCompanyLegalEntityId';

const keycloakInstance = () => {
  if (keycloakB2B && keycloakB2B.isAuthenticated) {
    return keycloakB2B;
  }

  return keycloak;
};
class Companies implements Module<CompanyState, any> {
  public state: CompanyState = {};
  public namespaced: boolean = true;
  public actions = {
    async getCompany(store: ActionContext<CompanyState, any>, id: number) {
      try {
        const company = await companyExposer.getCompany(id);
        store.commit('SET_COMPANY', company);
      } catch (_) {
        Vue.utils.$toast(t('error_msg_unknown'), 'error');
      }
    },

    async update(store: ActionContext<CompanyState, any>, params: { id: number; data: Partial<Company> }) {
      try {
        const company = await companyService.update(params.id, params.data);
        store.commit('SET_COMPANY', company);
      } catch (_) {
        Vue.utils.$toast(t('error_msg_unknown'), 'error');
      }
    },

    async getAddressValidation(store: ActionContext<CompanyState, any>) {
      if (store.state.addressValidation) {
        return; // this should cache the address validation after first load
      }

      try {
        const validaiton = await companyAddressService.getValidation(store.getters.companyIds[0]);
        store.commit('SET_ADDRESS_VALIDATION', validaiton);
      } catch (_) {
        Vue.utils.$toast(t('error_msg_unknown'), 'error');
      }
    },

    async getContactValidation(store: ActionContext<CompanyState, any>) {
      if (store.state.contactValidation) {
        return; // this should cache the contact validation after first load
      }

      try {
        const validaiton = await companyContactService.getValidation(store.getters.companyIds[0]);
        store.commit('SET_CONTACT_VALIDATION', validaiton);
      } catch (_) {
        Vue.utils.$toast(t('error_msg_unknown'), 'error');
      }
    },

    async getMyCompany(store: ActionContext<CompanyState, any>) {
      if (store.state.company) {
        return; // this should cache the company first loaded in bootstrap phase
      }

      try {
        const company = await companyExposer.getCompany(store.getters.companyIds[0]);
        store.commit('SET_COMPANY', company);
      } catch (error) {
        store.dispatch('app/showServerError', error, { root: true });
        throw error;
      }
    },

    async updateAddress(store: ActionContext<CompanyState, any>, payload: { id: number; address: Address; updateBillingAddress: boolean }) {
      try {
        const updatedCompany = await companyAddressService.update(payload.id, payload.address, payload.updateBillingAddress);
        store.commit('SET_COMPANY', updatedCompany);
      } catch (error) {
        store.dispatch('app/showServerError', error, { root: true });
        throw error;
      }
    },

    async updateContact(store: ActionContext<CompanyState, any>, payload: { id: number; contact: Contact }) {
      try {
        const updatedCompany = await companyContactService.update(payload.id, payload.contact);
        store.commit('SET_COMPANY', updatedCompany);
      } catch (error) {
        store.dispatch('app/showServerError', error, { root: true });
      }
    },
  } as ActionTree<CompanyState, any>;

  public mutations = {
    SET_COMPANY(state: CompanyState, company: Company) {
      Vue.set(state, 'company', company);
    },
    SET_ADDRESS_VALIDATION(state: CompanyState, addressValidation: AddressValidation) {
      state.addressValidation = addressValidation;
    },
    SET_CONTACT_VALIDATION(state: CompanyState, contactValidation: ContactValidation) {
      state.contactValidation = contactValidation;
    },
  } as MutationTree<CompanyState>;

  public getters = {
    isCompanyCentralPaymentProfile: () => {
      return this.state.company?.paymentProfileType !== CompanyType.NON_CENTRAL;
    },
    companyIds: () => {
      return keycloakInstance().companyIds;
    },
    currentCompanyId: () => {
      return keycloakInstance().companyIds[0];
    },
    currentCompanyLegalEntityId: () => this.state.company?.legalEntityId,
  } as GetterTree<any, any>;
}

export default new Companies();
